import { z } from 'zod';
import { AccountStatus, CompanyRole, Id, Limit, Palette, Seq, Timestamp } from './common';
import { GroupRole } from './conversations';
import { endpoint } from './endpoint';
import { lenient, lenientArray } from './lenient';
import { FileInfo } from './files';
import { LinkPreviewList } from './links';
import { HistoryMode, Message, MessageReactions, ScheduledMessage } from './messages';
import { InstanceState } from './server-info';
import { AvatarRef } from './users';

/**
 * Conversation-scoped change stream. `eventSeq` is a per-conversation
 * counter covering messages, edits, deletions, reads and membership changes.
 * Clients merge by eventSeq, ignore duplicates and fetch gaps via REST.
 */
export const ConversationEventData = z.discriminatedUnion('type', [
  z.object({ type: z.literal('message.created'), message: Message }),
  z.object({ type: z.literal('message.updated'), message: Message }),
  z.object({ type: z.literal('message.deleted'), message: Message }),
  /** Someone's read pointer moved; receipts on the viewer's messages may change. */
  z.object({ type: z.literal('read.updated'), userId: Id, lastReadSeq: Seq }),
  z.object({ type: z.literal('member.joined'), userIds: z.array(Id), actorId: Id, historyMode: HistoryMode }),
  z.object({ type: z.literal('member.left'), userId: Id }),
  z.object({ type: z.literal('member.removed'), userId: Id, actorId: Id }),
  z.object({ type: z.literal('member.role_changed'), userId: Id, role: lenient(GroupRole, 'member') }),
  z.object({ type: z.literal('conversation.updated'), title: z.string().nullable().optional(), historyMode: lenient(HistoryMode, 'after_join').optional() }),
  /** Scan result for an attachment already referenced by a message. */
  z.object({ type: z.literal('file.updated'), file: FileInfo }),
  /** `reactions.v1`: someone added or took back a reaction; the message's reactions as the viewer sees them now. Older apps skip it. */
  z.object({ type: z.literal('reaction.updated'), ...MessageReactions.shape }),
  /** The link preview cards of a message changed (fetched after sending, or removed by its author). */
  z.object({ type: z.literal('message.link_previews'), messageId: Id, linkPreviews: LinkPreviewList }),
]);
export type ConversationEventData = z.infer<typeof ConversationEventData>;

export const ConversationEvent = z.object({
  conversationId: Id,
  eventSeq: Seq,
  at: Timestamp,
  data: ConversationEventData,
});
export type ConversationEvent = z.infer<typeof ConversationEvent>;

/** Per-user stream: membership, multi-device state, session control. */
export const UserEventData = z.discriminatedUnion('type', [
  z.object({ type: z.literal('conversation.added'), conversationId: Id }),
  z.object({ type: z.literal('conversation.removed'), conversationId: Id }),
  z.object({
    type: z.literal('membership.updated'),
    conversationId: Id,
    muted: z.boolean(),
    pinned: z.boolean(),
    lastReadSeq: Seq,
  }),
  /** Upload owned by the viewer finished scanning (before it is attached to a message). */
  z.object({ type: z.literal('upload.updated'), file: FileInfo }),
  /** One of the viewer's scheduled messages was made, sent, failed or canceled (`messages.scheduled`). */
  z.object({ type: z.literal('scheduled.updated'), scheduled: ScheduledMessage }),
  /** The viewer saved a message, or took it out, on any of their devices (`messages.saved`). */
  z.object({ type: z.literal('message.saved'), conversationId: Id, messageId: Id, saved: z.boolean() }),
  z.object({ type: z.literal('invitations.changed') }),
  z.object({ type: z.literal('session.revoked'), sessionId: Id }),
  z.object({ type: z.literal('account.deactivated') }),
  /**
   * Someone's profile as others see it changed (name, department, status, photo), with each of those
   * fields as it is now. Every active account gets it; `role` goes only to the company admins and the
   * person. `avatar` absent: no photo, which is all that apps from before the other fields read.
   * Fields this app cannot read stay as they were. Apps without the event skip it.
   */
  z.object({
    type: z.literal('user.updated'),
    userId: Id,
    avatar: lenient(AvatarRef.optional(), undefined),
    displayName: lenient(z.string().optional(), undefined),
    shortName: lenient(z.string().optional(), undefined),
    department: lenient(z.string().optional(), undefined),
    palette: lenient(Palette.optional(), undefined),
    status: lenient(AccountStatus.optional(), undefined),
    role: lenient(CompanyRole.optional(), undefined),
    revision: lenient(z.number().int().nonnegative().optional(), undefined),
  }),
]);
export type UserEventData = z.infer<typeof UserEventData>;

export const UserEvent = z.object({ seq: Seq, at: Timestamp, data: UserEventData });
export type UserEvent = z.infer<typeof UserEvent>;

const EventsPageMeta = {
  /** Cursor to resume from, even when events were skipped because they are outside the viewer's history range. */
  nextAfter: Seq,
  hasMore: z.boolean(),
  /** The cursor is older than the replay window (7 days): refetch snapshots, then continue from `currentSeq`. */
  resetRequired: z.boolean(),
};

export const eventsEndpoints = {
  conversation: endpoint({
    id: 'events.conversation',
    method: 'GET',
    path: '/api/v1/conversations/:conversationId/events',
    auth: 'session',
    params: z.object({ conversationId: Id }),
    query: z.object({ after: Seq, limit: Limit(500, 200) }),
    /** Event types added by a newer server are skipped; `nextAfter` still moves past them. */
    response: z.object({ events: lenientArray(ConversationEvent), currentEventSeq: Seq, ...EventsPageMeta }),
  }),
  user: endpoint({
    id: 'events.user',
    method: 'GET',
    path: '/api/v1/me/events',
    auth: 'session',
    query: z.object({ after: Seq, limit: Limit(500, 200) }),
    response: z.object({ events: lenientArray(UserEvent), currentSeq: Seq, ...EventsPageMeta }),
  }),
};

/**
 * Socket.IO protocol. Writes always go through REST; the socket only delivers
 * committed changes. Auth: desktop sends `auth: { token }` (short-lived access
 * token); web relies on the session cookie plus an Origin check.
 */
export const SOCKET_PATH = '/api/v1/realtime';

export const SocketServerEvents = {
  conversationEvent: 'conv.event',
  userEvent: 'user.event',
  presence: 'presence',
  serverState: 'server.state',
  /** Someone else is typing in a conversation (`typing.v1`): shown a few seconds, never stored. */
  typing: 'typing',
} as const;

export const PresenceEvent = z.object({ userId: Id, online: z.boolean() });
export type PresenceEvent = z.infer<typeof PresenceEvent>;

export const TypingEvent = z.object({ conversationId: Id, userId: Id });
export type TypingEvent = z.infer<typeof TypingEvent>;

export const ServerStateEvent = z.object({ state: lenient(InstanceState, 'MAINTENANCE'), message: z.string().optional() });
export type ServerStateEvent = z.infer<typeof ServerStateEvent>;

/** Sent by the server right after a successful handshake. */
export const SocketReady = z.object({ userId: Id, sessionId: Id, onlineUserIds: z.array(Id) });
export const SOCKET_READY_EVENT = 'ready';
